/**
 * One ordered chain, shared by every repository that writes part of the same fact (R2-1, Slice R).
 *
 * The in-flight sitting is described by two records — where the mission was, and whose it is — and
 * everything that clears one clears the other. Two independent chains would let a clear of the
 * identity complete while the snapshot's own write was still in flight, so a launch could find a
 * snapshot with no identity beside it, refuse it, and cost a child the rounds they played. Handing
 * both repositories the *same* queue instance makes the ordering a property of the wiring rather
 * than of every caller's discipline.
 *
 * With today's LocalStorage-backed store the inner writes are synchronous, so the ordering this
 * adds is not yet observable; it becomes load-bearing the moment a write can actually be in flight
 * (IndexedDB, Phase 3 — flagged into the D-033 storage-review scope).
 *
 * The chain never holds a rejection — one failed write must not poison every later one — while
 * each caller still sees its own failure.
 */
export type WriteQueue = <T>(write: () => Promise<T>) => Promise<T>;

export function createWriteQueue(): WriteQueue {
  let writes: Promise<unknown> = Promise.resolve();

  return <T>(write: () => Promise<T>): Promise<T> => {
    const next = writes.then(write);
    writes = next.catch(() => {});
    return next;
  };
}
